import { observer } from "mobx-react";
import { alpha, Box, Card, CardContent, Grid, Skeleton, Typography } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FormSection from "../../../forms/FormSection";
import useFeature from "../../../useFeature";
import { useApplication } from "../../../ApplicationProvider";
import SummaryKpisFeature from "./SummaryKpisFeature";

const NO_VALUE = "—";

function formatPercentage(percentage) {
    if (percentage == null) {
        return NO_VALUE;
    }
    return `${Number(percentage).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function formatSignedPercentage(percentage) {
    const sign = percentage >= 0 ? "+" : "";
    return `${sign}${formatPercentage(percentage)}`;
}

function fulfillmentColor(fulfillment) {
    if (fulfillment == null) {
        return "text.secondary";
    }
    return fulfillment < 100 ? "error.main" : "success.main";
}

function deviationColor(deviation) {
    return deviation < 0 ? "error.main" : "success.main";
}

/**
 * Each card reads the summary KPIs coming from `dashboard/summary-kpis`:
 * { billingFulfillment, billingFulfillmentVsBudget, boxesFulfillment, boxesFulfillmentVsBudget,
 *   elapsedWorkingDays, totalWorkingDays, daysProgress }.
 * Fulfillments are percentages against the budget prorated up to today, and are null (along with their
 * deviation) when the period has no budget for the selected filters. `deviation` is what the card shows
 * next to its caption; a card without deviation (the days one) renders only the caption.
 */
const KPI_CARDS = [
    {
        key: "billingFulfillment",
        label: "Cumplimiento Facturación",
        icon: PaidOutlinedIcon,
        accentColor: "#D6A419",
        value: (summaryKpis) => formatPercentage(summaryKpis.billingFulfillment),
        valueColor: (summaryKpis) => fulfillmentColor(summaryKpis.billingFulfillment),
        deviation: (summaryKpis) => summaryKpis.billingFulfillmentVsBudget,
        caption: () => "vs. presupuesto a la fecha",
    },
    {
        key: "boxesFulfillment",
        label: "Cumplimiento Cajas",
        icon: Inventory2OutlinedIcon,
        accentColor: "#7E63C4",
        value: (summaryKpis) => formatPercentage(summaryKpis.boxesFulfillment),
        valueColor: (summaryKpis) => fulfillmentColor(summaryKpis.boxesFulfillment),
        deviation: (summaryKpis) => summaryKpis.boxesFulfillmentVsBudget,
        caption: () => "vs. presupuesto a la fecha",
    },
    {
        key: "daysProgress",
        label: "Avance del Período",
        icon: CalendarMonthOutlinedIcon,
        accentColor: "#3C8C6E",
        value: (summaryKpis) => `${summaryKpis.elapsedWorkingDays} / ${summaryKpis.totalWorkingDays}`,
        valueColor: () => "primary.main",
        deviation: () => null,
        caption: (summaryKpis) => `${formatPercentage(summaryKpis.daysProgress)} de los días hábiles`,
    },
];

function KpiCard({ label, icon: Icon, accentColor, value, valueColor, deviation, caption }) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Box>
                    <Typography color="textSecondary" variant="caption">
                        {label}
                    </Typography>
                    <Typography
                        component="div"
                        sx={(theme) => ({
                            fontFamily: theme.brand.fonts.display,
                            fontSize: "1.9rem",
                            lineHeight: 1.15,
                            color: valueColor,
                            mt: 0.5,
                        })}
                    >
                        {value}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.5 }}>
                        {deviation != null && (
                            <Typography variant="caption" sx={{ color: deviationColor(deviation), fontWeight: 600 }}>
                                {formatSignedPercentage(deviation)}
                            </Typography>
                        )}
                        <Typography color="textSecondary" variant="caption">
                            {caption}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        flexShrink: 0,
                        backgroundColor: alpha(accentColor, 0.15),
                        color: accentColor,
                    }}
                >
                    <Icon fontSize="small" />
                </Box>
            </CardContent>
        </Card>
    );
}

function SummaryKpisSkeleton() {
    return (
        <Grid container spacing={2}>
            {KPI_CARDS.map((kpiCard) => (
                <Grid item xs={12} sm={6} md={4} key={kpiCard.key}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={20} />
                            <Skeleton variant="text" width="40%" height={32} sx={{ mt: 0.5 }} />
                            <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
}

function SummaryKpisSection({ filters }) {
    const application = useApplication();
    const feature = useFeature(
        () => new SummaryKpisFeature(application, filters),
        [application, JSON.stringify(filters)]
    );

    return (
        <FormSection title="Resumen">
            {feature?.loaded ? (
                <Grid container spacing={2}>
                    {KPI_CARDS.map((kpiCard) => (
                        <Grid item xs={12} sm={6} md={4} key={kpiCard.key}>
                            <KpiCard
                                label={kpiCard.label}
                                icon={kpiCard.icon}
                                accentColor={kpiCard.accentColor}
                                value={kpiCard.value(feature.summaryKpis)}
                                valueColor={kpiCard.valueColor(feature.summaryKpis)}
                                deviation={kpiCard.deviation(feature.summaryKpis)}
                                caption={kpiCard.caption(feature.summaryKpis)}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <SummaryKpisSkeleton />
            )}
        </FormSection>
    );
}

export default observer(SummaryKpisSection);
